import type { QueryClient } from '@tanstack/react-query';
import { dialogueVoiceoversKeys } from '@/hooks/api/useDialogueVoiceovers';
import type { DialogueVoiceoverResponse } from '@/services/api/storyboard-resources';
import { useCollaborationHighlightStore } from '@/stores/collaborationHighlightStore';

export function removeDeletedStoryboardVoiceoverFromCache(
  queryClient: QueryClient,
  params: {
    storyboardId: string;
    voiceoverId: string;
  }
): void {
  const { storyboardId, voiceoverId } = params;
  const listKey = dialogueVoiceoversKeys.storyboard(storyboardId);

  queryClient.setQueryData<DialogueVoiceoverResponse[]>(listKey, (current = []) =>
    current.filter((item) => item.voiceoverId !== voiceoverId)
  );

  useCollaborationHighlightStore.getState().clearHighlight({
    targetType: 'voiceover',
    targetId: voiceoverId,
  });
}

export function upsertStoryboardVoiceoverCache(
  queryClient: QueryClient,
  voiceover: DialogueVoiceoverResponse
): void {
  const listKey = dialogueVoiceoversKeys.storyboard(voiceover.storyboardId);

  queryClient.setQueryData<DialogueVoiceoverResponse[]>(listKey, (current = []) => {
    const next = current.some((item) => item.voiceoverId === voiceover.voiceoverId)
      ? current.map((item) => (item.voiceoverId === voiceover.voiceoverId ? voiceover : item))
      : [...current, voiceover];
    return next.sort((a, b) => {
      const aCreatedAt = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreatedAt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bCreatedAt - aCreatedAt;
    });
  });
}

export function applyStoryboardVoiceoverDeletedCacheSync(
  queryClient: QueryClient,
  payload: {
    storyboardId?: string | null;
    entityId?: string | null;
  }
): void {
  const storyboardId = payload.storyboardId?.trim();
  const voiceoverId = payload.entityId?.trim();
  if (!storyboardId || !voiceoverId) return;

  removeDeletedStoryboardVoiceoverFromCache(queryClient, { storyboardId, voiceoverId });
}
